/**
 * Cloudflare Proxy System Tester - Umfassende Tests für das Unlimited Domain System
 */

import { 
    testCloudflareWorker,
    connectDomainToSpaceWithProxy,
    getUserCloudflareMapping,
    registerDomainWithProxy 
} from './domainHelpers';

class CloudflareProxyTester {
    constructor() {
        this.testResults = [];
    }

    log(message, type = 'info') {
        const timestamp = new Date().toISOString();
        const logEntry = { timestamp, message, type };
        this.testResults.push(logEntry);
        
        const emoji = {
            'info': 'ℹ️',
            'success': '✅',
            'error': '❌',
            'warning': '⚠️',
            'test': '🧪'
        };
        
        console.log(`${emoji[type]} [${timestamp}] ${message}`);
    }

    async testWorkerConnectivity() {
        this.log('Testing Cloudflare Worker connectivity...', 'test');
        
        try {
            const result = await testCloudflareWorker();
            
            if (result.success) {
                this.log(`Worker is online: ${result.workerUrl}`, 'success');
                this.log(`Response: ${result.response}`, 'info');
                return true;
            } else {
                this.log(`Worker test failed: ${result.error}`, 'error');
                return false;
            }
        } catch (error) {
            this.log(`Worker connectivity error: ${error.message}`, 'error');
            return false;
        }
    }

    async testDomainRegistration(testDomain = 'test-domain-123.example.com', userId = 'test-user', username = 'testspace', spaceId = 'test-space') {
        this.log(`Testing domain registration: ${testDomain}`, 'test');
        
        try {
            const result = await registerDomainWithProxy(testDomain, userId, username, spaceId);
            
            if (result.success) {
                this.log(`Domain registered successfully in proxy system`, 'success');
                this.log(`KV Mapping created: ${JSON.stringify(result.kvMapping)}`, 'info');
                
                if (result.instructions) {
                    this.log(`DNS Instructions available: ${result.instructions.dns.length} records`, 'info');
                }
                
                return true;
            } else {
                this.log(`Domain registration failed`, 'error');
                return false;
            }
        } catch (error) {
            this.log(`Domain registration error: ${error.message}`, 'error');
            return false;
        }
    }

    async testUserMappingRetrieval(userId = 'test-user') {
        this.log(`Testing user mapping retrieval for: ${userId}`, 'test');
        
        try {
            const mappings = await getUserCloudflareMapping(userId);
            
            this.log(`Retrieved ${mappings.length} domain mappings`, 'success');
            
            if (mappings.length > 0) {
                mappings.forEach(mapping => {
                    this.log(`Mapping: ${mapping.domain} → ${mapping.username}`, 'info');
                });
            }
            
            return mappings;
        } catch (error) {
            this.log(`User mapping retrieval error: ${error.message}`, 'error');
            return [];
        }
    }

    async testProxySystemIntegration(testDomain = 'integration-test.example.com') {
        this.log(`Testing full proxy system integration with: ${testDomain}`, 'test');
        
        const testUserId = 'integration-test-user';
        const testUsername = 'integrationtest';
        const testSpaceId = 'integration-space';
        const testSpaceName = 'Integration Test Space';
        
        try {
            // Test full integration with connectDomainToSpaceWithProxy
            const result = await connectDomainToSpaceWithProxy(
                testUserId, 
                testSpaceId, 
                testDomain, 
                testSpaceName, 
                testUsername
            );
            
            if (result.proxyEnabled) {
                this.log(`Integration test successful - Proxy enabled`, 'success');
                this.log(`Proxy URL: ${result.proxyUrl}`, 'info');
                
                if (result.instructions) {
                    this.log(`DNS Instructions generated successfully`, 'success');
                    result.instructions.dns.forEach(record => {
                        this.log(`DNS: ${record.type} ${record.name} → ${record.value}`, 'info');
                    });
                }
                
                return true;
            } else {
                this.log(`Integration test failed - Proxy not enabled`, 'warning');
                this.log(`Fallback message: ${result.warning}`, 'info');
                return false;
            }
        } catch (error) {
            this.log(`Integration test error: ${error.message}`, 'error');
            return false;
        }
    }

    async testCloudflareWorkerDirectly() {
        this.log('Testing Cloudflare Worker directly via fetch...', 'test');
        
        const workerUrl = 'https://bonusclub-proxy.bonusclub.workers.dev';
        
        try {
            const response = await fetch(workerUrl, {
                method: 'GET',
                headers: {
                    'User-Agent': 'BonusClub-Integration-Test'
                }
            });
            
            const text = await response.text();
            
            if (response.ok) {
                this.log(`Direct worker test successful (${response.status})`, 'success');
                this.log(`Worker response: ${text.substring(0, 100)}...`, 'info');
                return true;
            } else {
                this.log(`Direct worker test failed (${response.status})`, 'error');
                return false;
            }
        } catch (error) {
            this.log(`Direct worker test error: ${error.message}`, 'error');
            return false;
        }
    }

    async testWorkerViaHTTPEndpoint() {
        this.log('Testing Cloudflare Worker via HTTP endpoint (CORS-enabled)...', 'test');
        
        const httpEndpoint = 'https://us-central1-bonusclub-dev.cloudfunctions.net/testCloudflareWorkerHTTP';
        
        try {
            const response = await fetch(httpEndpoint, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            const result = await response.json();
            
            if (result.success) {
                this.log(`HTTP endpoint test successful`, 'success');
                this.log(`Worker URL: ${result.workerUrl}`, 'info');
                this.log(`Worker response: ${result.response}`, 'info');
                return true;
            } else {
                this.log(`HTTP endpoint test failed: ${result.error}`, 'error');
                return false;
            }
        } catch (error) {
            this.log(`HTTP endpoint test error: ${error.message}`, 'error');
            return false;
        }
    }

    async runCompleteTest() {
        this.log('🚀 Starting complete Cloudflare Proxy System test...', 'test');
        this.testResults = [];
        
        const tests = [
            { name: 'Direct Worker Access', test: () => this.testCloudflareWorkerDirectly() },
            { name: 'Worker via HTTP Endpoint', test: () => this.testWorkerViaHTTPEndpoint() },
            { name: 'Worker Connectivity (Firebase)', test: () => this.testWorkerConnectivity() },
            { name: 'Domain Registration', test: () => this.testDomainRegistration() },
            { name: 'User Mapping Retrieval', test: () => this.testUserMappingRetrieval() },
            { name: 'Full Integration', test: () => this.testProxySystemIntegration() }
        ];
        
        let passedTests = 0;
        const totalTests = tests.length;
        
        for (const { name, test } of tests) {
            this.log(`\n--- Running Test: ${name} ---`, 'test');
            
            try {
                const result = await test();
                if (result) {
                    passedTests++;
                    this.log(`✅ ${name} PASSED`, 'success');
                } else {
                    this.log(`❌ ${name} FAILED`, 'error');
                }
            } catch (error) {
                this.log(`❌ ${name} ERROR: ${error.message}`, 'error');
            }
        }
        
        this.log(`\n🎯 Test Summary: ${passedTests}/${totalTests} tests passed`, passedTests === totalTests ? 'success' : 'warning');
        
        if (passedTests === totalTests) {
            this.log('🎉 All tests passed! Cloudflare Proxy System is fully operational.', 'success');
        } else {
            this.log(`⚠️ ${totalTests - passedTests} tests failed. Check configuration and try again.`, 'warning');
        }
        
        return {
            passed: passedTests,
            total: totalTests,
            success: passedTests === totalTests,
            results: this.testResults
        };
    }

    async testRealDomainSetup(domain, userId, username, spaceId, spaceName) {
        this.log(`🌐 Testing real domain setup: ${domain}`, 'test');
        
        if (!domain || !userId || !username || !spaceId) {
            this.log('Missing required parameters for real domain test', 'error');
            return false;
        }
        
        try {
            // Test the full flow with real domain
            const result = await connectDomainToSpaceWithProxy(
                userId, 
                spaceId, 
                domain, 
                spaceName || username, 
                username
            );
            
            if (result.proxyEnabled) {
                this.log(`Real domain setup successful! 🎉`, 'success');
                this.log(`Domain: ${domain}`, 'info');
                this.log(`Proxy URL: ${result.proxyUrl}`, 'info');
                
                this.log('\n📋 DNS Setup Instructions:', 'info');
                result.instructions.dns.forEach(record => {
                    this.log(`Add ${record.type} record: ${record.name} → ${record.value}`, 'info');
                });
                
                this.log('\n⏱️ Next Steps:', 'info');
                this.log('1. Add the DNS record above at your domain provider', 'info');
                this.log('2. Wait 2-5 minutes for DNS propagation', 'info');
                this.log(`3. Test your domain: https://${domain}`, 'info');
                
                return true;
            } else {
                this.log(`Real domain setup failed - using fallback`, 'warning');
                return false;
            }
        } catch (error) {
            this.log(`Real domain setup error: ${error.message}`, 'error');
            return false;
        }
    }

    getTestResults() {
        return this.testResults;
    }

    printSummary() {
        const summary = this.testResults.reduce((acc, result) => {
            acc[result.type] = (acc[result.type] || 0) + 1;
            return acc;
        }, {});
        
        console.log('\n📊 Test Results Summary:');
        Object.entries(summary).forEach(([type, count]) => {
            console.log(`${type}: ${count}`);
        });
    }
}

// Global instance for browser console testing
window.cloudflareProxyTester = new CloudflareProxyTester();

// Export for use in React components
export default CloudflareProxyTester;

// Convenience functions for browser console
export const testCloudflareProxySystem = () => {
    return window.cloudflareProxyTester.runCompleteTest();
};

export const testRealDomain = (domain, userId, username, spaceId, spaceName) => {
    return window.cloudflareProxyTester.testRealDomainSetup(domain, userId, username, spaceId, spaceName);
};

